import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  BriefcaseBusiness,
  RefreshCw,
  Eye,
  User,
  CalendarDays,
  FileText,
  MapPin,
  Mail,
  Phone,
  Clock3,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Search,
  ChevronDown,
  X,
} from "lucide-react";
import PartnerHeader from "../../component/PartnerHeader";
import PartnerFooter from "../../component/PartnerFooter";

const API_BASE = "/api";

const PARTNER_APPLICATIONS_API = (partnerId) =>
  `${API_BASE}/job-applications/partner/${partnerId}`;

const PARTNER_JOB_APPLICATIONS_API = (partnerId, jobId) =>
  `${API_BASE}/job-applications/partner/${partnerId}/job/${jobId}`;

const JOB_APPLICATIONS_API = (jobId) =>
  `${API_BASE}/job-applications/job/${jobId}`;

// Returns ALL partners; we find the matching one by email on the
// frontend since there's no dedicated by-email lookup endpoint.
const ALL_PARTNERS_API = () => `${API_BASE}/partners/all`;

const STATUS_OPTIONS = [
  "ALL",
  "PENDING",
  "SHORTLISTED",
  "INTERVIEW",
  "SELECTED",
  "REJECTED",
  "WITHDRAWN",
  "NOT_DISCLOSED",
];

const COLORS = {
  primary: "#3E0F8D",
  secondary: "#9564DD",
  yellow: "#E4DA72",
  light: "#EEEEEE",
  white: "#FFFFFF",
  text: "#252525",
  muted: "#6B7280",
  border: "#E5E7EB",
  success: "#15803D",
  danger: "#DC2626",
  warning: "#D97706",
  info: "#2563EB",
};

const getToken = () => {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
};

/**
 * IMPORTANT: The value that used to be read from localStorage
 * (user.id / user.userId) is the logged-in USER's id, not the
 * partner id. To get the real partner id, we must:
 *   1. Read the logged-in user's email from localStorage.
 *   2. Look that email up against the partner table via
 *      ALL_PARTNERS_API to get the actual partnerId.
 *
 * Supported localStorage user shapes for email:
 * { email: "x@y.com" }
 * { user: { email: "x@y.com" } }
 * { partner: { email: "x@y.com" } }
 */
const getLoggedInUser = () => {
  try {
    const userString = localStorage.getItem("user");

    if (!userString) {
      console.warn("No user found in localStorage");
      return null;
    }

    const user = JSON.parse(userString);

    console.log("LOGGED IN USER:", user);

    return user;
  } catch (error) {
    console.error("Unable to parse logged-in user:", error);
    return null;
  }
};

const getLoggedInUserEmail = (user) => {
  if (!user) return null;

  return (
    user?.email ||
    user?.user?.email ||
    user?.partner?.email ||
    user?.userEmail ||
    null
  );
};

const getLoggedInUserId = (user) => {
  if (!user) return null;

  return user?.id ?? user?.userId ?? user?.user?.id ?? null;
};


/**
 * Fetches ALL partners from /partners/all and finds the one whose
 * email matches the logged-in user's email, then returns its
 * partnerId. Email match is case-insensitive.
 */
const resolvePartnerIdByEmail = async (email, token) => {
  if (!email) return null;

  const headers = {
    Accept: "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(ALL_PARTNERS_API(), {
    method: "GET",
    headers,
  });

  const text = await response.text();

  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    const backendMessage =
      data?.message ||
      data?.error ||
      text ||
      `Partner lookup failed with status ${response.status}`;

    throw new Error(backendMessage);
  }

  const partners = normalizeArrayResponse(data);

  const targetEmail = String(email).trim().toLowerCase();

  const matchedPartner = partners.find((partner) => {
    const partnerEmail =
      partner?.email || partner?.partnerEmail || partner?.emailId || "";

    return String(partnerEmail).trim().toLowerCase() === targetEmail;
  });

  const partnerId =
    matchedPartner?.partnerId ??
    matchedPartner?.id ??
    matchedPartner?.partner_id ??
    null;

  console.log("RESOLVED PARTNER ID FROM EMAIL:", partnerId);

  return partnerId;
};

const normalizeArrayResponse = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.content)) return data.content;

  if (Array.isArray(data?.applications)) return data.applications;

  if (Array.isArray(data?.results)) return data.results;

  return [];
};

const getApplicationId = (application) =>
  application?.applicationId ??
  application?.id ??
  application?.application_id;

const getJobId = (application) =>
  application?.jobId ??
  application?.job?.jobId ??
  application?.job?.id ??
  application?.job_id;

const getTrainerId = (application) =>
  application?.trainerId ??
  application?.trainer?.trainerId ??
  application?.trainer?.id ??
  application?.trainer_id;

const getTrainerName = (application) => {
  const trainer = application?.trainer;

  return (
    application?.trainerName ||
    application?.fullName ||
    application?.trainerFullName ||
    trainer?.fullName ||
    trainer?.name ||
    trainer?.trainerName ||
    "Trainer"
  );
};

const getTrainerEmail = (application) => {
  const trainer = application?.trainer;

  return (
    application?.trainerEmail ||
    application?.email ||
    trainer?.email ||
    trainer?.trainerEmail ||
    "—"
  );
};

const getTrainerPhone = (application) => {
  const trainer = application?.trainer;

  return (
    application?.trainerPhone ||
    application?.phone ||
    application?.mobile ||
    trainer?.phone ||
    trainer?.mobile ||
    trainer?.phoneNumber ||
    "—"
  );
};

const getJobTitle = (application) => {
  const job = application?.job;

  return (
    application?.jobTitle ||
    application?.title ||
    job?.jobTitle ||
    job?.title ||
    `Job #${getJobId(application) || "—"}`
  );
};

const getJobLocation = (application) => {
  const job = application?.job;

  return (
    application?.jobLocation ||
    application?.location ||
    job?.location ||
    "—"
  );
};

const getSkills = (application) => {
  const job = application?.job;

  const skills =
    application?.skillsRequired ||
    application?.skills ||
    job?.skillsRequired ||
    job?.skills ||
    application?.technology ||
    "";

  if (Array.isArray(skills)) {
    return skills;
  }

  if (typeof skills === "string") {
    return skills
      .split(",")
      .map((skill) => skill.trim())
      .filter(Boolean);
  }

  return [];
};

const getExperience = (application) => {
  const trainer = application?.trainer;
  const job = application?.job;

  return (
    application?.trainerExperience ||
    application?.experience ||
    trainer?.experience ||
    trainer?.totalExperience ||
    job?.experienceRequired ||
    application?.experienceRequired ||
    "—"
  );
};

const getAppliedDate = (application) => {
  return (
    application?.appliedAt ||
    application?.createdAt ||
    application?.applied_at ||
    null
  );
};

const formatDate = (dateValue) => {
  if (!dateValue) return "—";

  try {
    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return String(dateValue);
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(dateValue);
  }
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return "—";

  try {
    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return String(dateValue);
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateValue);
  }
};
const getApplicationStatus = (application) =>
  normalizeStatus(
    application?.applicationStatus ??
    application?.status
  );

const normalizeStatus = (status) => {
  if (!status) return "PENDING";

  return String(status)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
};

const getStatusLabel = (status) => {
  const normalized = normalizeStatus(status);

  return normalized
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusConfig = (status) => {
  const normalized = normalizeStatus(status);

  switch (normalized) {
    case "SELECTED":
      return {
        background: "#DCFCE7",
        color: COLORS.success,
        icon: CheckCircle2,
      };

    case "REJECTED":
      return {
        background: "#FEE2E2",
        color: COLORS.danger,
        icon: XCircle,
      };

    case "SHORTLISTED":
      return {
        background: "#DBEAFE",
        color: COLORS.info,
        icon: CheckCircle2,
      };

    case "WITHDRAWN":
      return {
        background: "#F3F4F6",
        color: COLORS.muted,
        icon: XCircle,
      };

    case "INTERVIEW":
    case "INTERVIEW_SCHEDULED":
      return {
        background: "#FEF3C7",
        color: COLORS.warning,
        icon: CalendarDays,
      };

    case "PENDING":
    default:
      return {
        background: "#FEF3C7",
        color: COLORS.warning,
        icon: Clock3,
      };
      case "NOT_DISCLOSED":
  return {
    background: "#F3F4F6",
    color: "#4B5563",
    icon: AlertCircle,
  };
  }
};

const StatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "6px 10px",
        borderRadius: "999px",
        background: config.background,
        color: config.color,
        fontSize: "12px",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={13} />
      {getStatusLabel(status)}
    </span>
  );
};


const JobApplication = ({ jobId = null, jobOnly = false }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [partnerId, setPartnerId] = useState(null);
  const [updatingApplicationId, setUpdatingApplicationId] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [selectedApplication, setSelectedApplication] = useState(null);

  const fetchApplications = useCallback(
    async (showRefreshLoader = false) => {
      /**
       * Default behaviour (jobOnly=false):
       * 1. Read the logged-in user from localStorage.
       * 2. Get that user's email.
       * 3. Look the email up against the partner table to get the
       *    REAL partnerId (localStorage only has the user id).
       * 4. Fetch through the partner-scoped endpoints using that
       *    resolved partnerId:
       *      - no jobId   -> GET /job-applications/partner/{partnerId}
       *      - with jobId -> GET /job-applications/partner/{partnerId}/job/{jobId}
       *
       * Opt-in (jobOnly=true):
       * Skip the user/email/partner lookup entirely and call
       * GET /job-applications/job/{jobId} directly.
       */
      const useJobOnlyEndpoint = Boolean(jobId) && jobOnly;

      let resolvedPartnerId = null;
      const token = getToken();

      if (!useJobOnlyEndpoint) {
        const loggedInUser = getLoggedInUser();
        const userEmail = getLoggedInUserEmail(loggedInUser);

        if (!userEmail) {
          setPartnerId(null);
          setApplications([]);
          setLoading(false);
          setRefreshing(false);
          setError(
            "Logged-in user's email not found. Please login again or check localStorage user data."
          );
          return;
        }

        try {
          resolvedPartnerId = await resolvePartnerIdByEmail(
            userEmail,
            token
          );
        } catch (lookupErr) {
          console.error("PARTNER LOOKUP ERROR:", lookupErr);

          setPartnerId(null);
          setApplications([]);
          setLoading(false);
          setRefreshing(false);
          setError(
            lookupErr?.message ||
              "Unable to resolve partner from logged-in user's email."
          );
          return;
        }

        if (!resolvedPartnerId) {
          setPartnerId(null);
          setApplications([]);
          setLoading(false);
          setRefreshing(false);
          setError(
            `No partner found for email ${userEmail}. Please check the partner table.`
          );
          return;
        }

        setPartnerId(resolvedPartnerId);
      } else {
        setPartnerId(null);
      }

      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const url = useJobOnlyEndpoint
          ? JOB_APPLICATIONS_API(jobId)
          : jobId
          ? PARTNER_JOB_APPLICATIONS_API(resolvedPartnerId, jobId)
          : PARTNER_APPLICATIONS_API(resolvedPartnerId);

        console.log("FETCHING JOB APPLICATIONS:", url);

        const headers = {
          Accept: "application/json",
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(url, {
          method: "GET",
          headers,
        });

        const text = await response.text();

        let data = [];

        try {
          data = text ? JSON.parse(text) : [];
        } catch {
          data = [];
        }

        if (!response.ok) {
          const backendMessage =
            data?.message ||
            data?.error ||
            text ||
            `Request failed with status ${response.status}`;

          throw new Error(backendMessage);
        }

        let result = normalizeArrayResponse(data);

        /**
         * Extra frontend safety:
         * If jobId is provided, only display that job.
         */
        if (jobId) {
          result = result.filter(
            (application) => String(getJobId(application)) === String(jobId)
          );
        }

        setApplications(result);

        console.log("JOB APPLICATIONS:", result);
      } catch (err) {
        console.error("APPLICATION FETCH ERROR:", err);

        setApplications([]);
        setError(
          err?.message || "Unable to load job applications."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [jobId, jobOnly]
  );

  useEffect(() => {
    fetchApplications(false);
  }, [fetchApplications]);

  const updateApplicationInState = (applicationId, status) => {
  setApplications((previous) =>
    previous.map((item) =>
      String(getApplicationId(item)) === String(applicationId)
        ? {
            ...item,
            applicationStatus: status,
            status,
          }
        : item
    )
  );

  // Keep an already-open detail modal in sync too.
  setSelectedApplication((previous) =>
    previous &&
    String(getApplicationId(previous)) === String(applicationId)
      ? {
          ...previous,
          applicationStatus: status,
          status,
        }
      : previous
  );
};

const readResponse = async (response) => {
  const text = await response.text();

  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }

  if (!response.ok) {
    throw new Error(
      data?.message ||
      data?.error ||
      `Request failed with status ${response.status}`
    );
  }

  return data;
};

const getRequestHeaders = () => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const sendInterviewInvitation = async (application) => {
  const applicationId = getApplicationId(application);
  const trainerId = getTrainerId(application);

  // This is the logged-in USER ID, not partnerId.
  const loggedInUser = getLoggedInUser();
  const partnerUserId = getLoggedInUserId(loggedInUser);

  if (!applicationId || !trainerId || !partnerUserId) {
    await Swal.fire({
      icon: "error",
      title: "Missing information",
      text: "Could not identify the application, trainer, or logged-in partner user.",
      confirmButtonColor: COLORS.primary,
    });
    return;
  }

  const result = await Swal.fire({
    title: "Schedule an interview",
    confirmButtonText: "Send Invitation",
    cancelButtonText: "Cancel",
    showCancelButton: true,
    confirmButtonColor: COLORS.primary,
    focusConfirm: false,
    html: `
      <div style="text-align:left">
        <p style="margin:0 0 12px;color:#6B7280;font-size:13px">
          Invitation for <strong>${getTrainerName(application)}</strong>
        </p>

        <label for="interview-date" style="display:block;margin-bottom:5px">
          Interview date and time
        </label>
        <input id="interview-date" type="datetime-local"
          class="swal2-input" style="width:100%;margin:0 0 14px" />

        <label for="interview-mode" style="display:block;margin-bottom:5px">
          Interview mode
        </label>
        <select id="interview-mode" class="swal2-select"
          style="width:100%;margin:0 0 14px">
          <option value="ONLINE">Online</option>
          <option value="OFFLINE">In person</option>
        </select>

        <label for="interview-location" style="display:block;margin-bottom:5px">
          Meeting link or venue
        </label>
        <input id="interview-location" class="swal2-input"
          placeholder="Meeting URL or office address"
          style="width:100%;margin:0 0 14px" />

        <label for="interview-message" style="display:block;margin-bottom:5px">
          Message (optional)
        </label>
        <textarea id="interview-message" class="swal2-textarea"
          placeholder="Interview instructions"
          style="width:100%;margin:0"></textarea>
      </div>
    `,
    preConfirm: () => {
      const interviewDateTime =
        document.getElementById("interview-date").value;
      const mode =
        document.getElementById("interview-mode").value;
      const meetingLinkOrVenue =
        document.getElementById("interview-location").value.trim();
      const message =
        document.getElementById("interview-message").value.trim();

      if (!interviewDateTime) {
        Swal.showValidationMessage("Choose the interview date and time.");
        return false;
      }

      if (new Date(interviewDateTime).getTime() <= Date.now()) {
        Swal.showValidationMessage("Choose a future date and time.");
        return false;
      }

      if (!meetingLinkOrVenue) {
        Swal.showValidationMessage("Enter the meeting link or venue.");
        return false;
      }

      if (
        mode === "ONLINE" &&
        !/^https?:\/\/\S+/i.test(meetingLinkOrVenue)
      ) {
        Swal.showValidationMessage(
          "Enter a valid online meeting URL starting with http:// or https://."
        );
        return false;
      }

      return {
        interviewDateTime,
        mode,
        meetingLinkOrVenue,
        message,
      };
    },
  });

  if (!result.isConfirmed) return;

  setUpdatingApplicationId(applicationId);

  try {
    Swal.fire({
      title: "Sending interview invitation...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    /*
     * This endpoint must be implemented in your Spring Boot backend.
     * It should verify job ownership, persist the interview details,
     * update application status to INTERVIEW, and notify the trainer.
     */
    const response = await fetch(
      `${API_BASE}/job-applications/${applicationId}/interview-invite`,
      {
        method: "POST",
        headers: getRequestHeaders(),
        body: JSON.stringify({
          trainerId,
          partnerUserId,
          partnerId,
          jobId: getJobId(application),
          interviewDateTime: result.value.interviewDateTime,
          mode: result.value.mode,
          meetingLinkOrVenue: result.value.meetingLinkOrVenue,
          message: result.value.message,
        }),
      }
    );

    await readResponse(response);

    updateApplicationInState(applicationId, "INTERVIEW");

    await Swal.fire({
      icon: "success",
      title: "Interview invitation sent",
      text: `The invitation for ${getTrainerName(application)} was submitted successfully.`,
      confirmButtonColor: COLORS.primary,
    });

    // Reload the backend record so the table displays the saved status.
    await fetchApplications(true);
  } catch (error) {
    await Swal.fire({
      icon: "error",
      title: "Could not send invitation",
      text: error.message || "Please try again.",
      confirmButtonColor: COLORS.primary,
    });
  } finally {
    setUpdatingApplicationId(null);
  }
};

const handleApplicationStatusChange = async (application, nextStatus) => {
  const currentStatus = getApplicationStatus(application);

  if (nextStatus === currentStatus) return;

  // Interview is a special flow: confirm details before changing status.
  if (nextStatus === "INTERVIEW") {
    await sendInterviewInvitation(application);
    return;
  }

  const applicationId = getApplicationId(application);

  if (!applicationId) {
    await Swal.fire("Error", "Application ID is missing.", "error");
    return;
  }

  const confirmation = await Swal.fire({
    title: "Update application status?",
    text: `Change the status to ${getStatusLabel(nextStatus)}?`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Update Status",
    cancelButtonText: "Cancel",
    confirmButtonColor: COLORS.primary,
  });

  if (!confirmation.isConfirmed) return;

  setUpdatingApplicationId(applicationId);

  try {
    const response = await fetch(
      `${API_BASE}/job-applications/${applicationId}/status`,
      {
        method: "PATCH",
        headers: getRequestHeaders(),
        body: JSON.stringify({
  applicationStatus: nextStatus,
}),
      }
    );

    await readResponse(response);
    updateApplicationInState(applicationId, nextStatus);

    await Swal.fire({
      icon: "success",
      title: "Status updated",
      text: `Application marked as ${getStatusLabel(nextStatus)}.`,
      confirmButtonColor: COLORS.primary,
    });

    await fetchApplications(true);
  } catch (error) {
    await Swal.fire({
      icon: "error",
      title: "Status update failed",
      text: error.message || "Please try again.",
      confirmButtonColor: COLORS.primary,
    });
  } finally {
    setUpdatingApplicationId(null);
  }
};

  const filteredApplications = useMemo(() => {
    let result = [...applications];

   if (statusFilter !== "ALL") {
  result = result.filter(
    (application) => getApplicationStatus(application) === statusFilter
  );
}

    const searchValue = search.trim().toLowerCase();

    if (searchValue) {
      result = result.filter((application) => {
        const values = [
          getApplicationId(application),
          application?.applicationNumber,
          getTrainerId(application),
          getTrainerName(application),
          getTrainerEmail(application),
          getTrainerPhone(application),
          getJobTitle(application),
          getJobLocation(application),
          getExperience(application),
          application?.coverLetter,
          application?.remarks,
          ...getSkills(application),
        ];

        return values.some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(searchValue)
        );
      });
    }

    return result;
  }, [applications, search, statusFilter]);

  const summary = useMemo(() => {
    const total = applications.length;

    const pending = applications.filter(
  (item) => getApplicationStatus(item) === "PENDING"
).length;

const shortlisted = applications.filter(
  (item) => getApplicationStatus(item) === "SHORTLISTED"
).length;

const selected = applications.filter(
  (item) => getApplicationStatus(item) === "SELECTED"
).length;

const rejected = applications.filter(
  (item) => getApplicationStatus(item) === "REJECTED"
).length;

    return {
      total,
      pending,
      shortlisted,
      selected,
      rejected,
    };
  }, [applications]);

  const openApplication = (application) => {
    setSelectedApplication(application);
  };

  const closeApplication = () => {
    setSelectedApplication(null);
  };

  return (<>
   <PartnerHeader/>

    <div
      style={{
        minHeight: "100vh",
        background: "#F7F7FA",
        padding: "24px",
        color: COLORS.text,
        boxSizing: "border-box",
      }}
    >
     
      {/* HEADER */}
      <div
        style={{
          background: COLORS.white,
          borderRadius: "16px",
          padding: "22px 24px",
          marginBottom: "20px",
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "15px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "6px",
              }}
            >
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "11px",
                  background: COLORS.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: COLORS.white,
                }}
              >
                <BriefcaseBusiness size={22} />
              </div>

              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: 800,
                  color: COLORS.primary,
                }}
              >
                Job Applications
              </h1>
            </div>

            <p
              style={{
                margin: "4px 0 0 52px",
                color: COLORS.muted,
                fontSize: "14px",
              }}
            >
              {jobId
                ? `All applications received for Job #${jobId}`
                : "All applications received for your posted jobs"}
            </p>

            {partnerId && (
              <div
                style={{
                  marginLeft: "52px",
                  marginTop: "8px",
                  fontSize: "12px",
                  color: COLORS.muted,
                }}
              >
                Partner ID: <strong>{partnerId}</strong>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => fetchApplications(true)}
            disabled={loading || refreshing}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              border: "none",
              borderRadius: "9px",
              padding: "10px 16px",
              background: COLORS.primary,
              color: COLORS.white,
              cursor:
                loading || refreshing ? "not-allowed" : "pointer",
              opacity: loading || refreshing ? 0.7 : 1,
              fontWeight: 700,
            }}
          >
            <RefreshCw
              size={16}
              style={{
                animation: refreshing ? "spin 1s linear infinite" : "none",
              }}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* SUMMARY */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "15px",
          marginBottom: "20px",
        }}
      >
        <SummaryCard
          title="Total Applications"
          value={summary.total}
          icon={FileText}
          background="#F3E8FF"
          iconColor={COLORS.primary}
        />

        <SummaryCard
          title="Pending"
          value={summary.pending}
          icon={Clock3}
          background="#FEF3C7"
          iconColor={COLORS.warning}
        />

        <SummaryCard
          title="Shortlisted"
          value={summary.shortlisted}
          icon={CheckCircle2}
          background="#DBEAFE"
          iconColor={COLORS.info}
        />

        <SummaryCard
          title="Selected"
          value={summary.selected}
          icon={CheckCircle2}
          background="#DCFCE7"
          iconColor={COLORS.success}
        />

        <SummaryCard
          title="Rejected"
          value={summary.rejected}
          icon={XCircle}
          background="#FEE2E2"
          iconColor={COLORS.danger}
        />
      </div>

      {/* FILTERS */}
      <div
        style={{
          background: COLORS.white,
          border: `1px solid ${COLORS.border}`,
          borderRadius: "14px",
          padding: "16px",
          marginBottom: "18px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              position: "relative",
              flex: "1 1 280px",
            }}
          >
            <Search
              size={18}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: COLORS.muted,
              }}
            />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trainer, job, email, phone, skills..."
              style={{
                width: "100%",
                boxSizing: "border-box",
                height: "42px",
                padding: "0 14px 0 40px",
                borderRadius: "9px",
                border: `1px solid ${COLORS.border}`,
                outline: "none",
                fontSize: "14px",
              }}
            />
          </div>

          <div
            style={{
              position: "relative",
              minWidth: "180px",
            }}
          >
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                appearance: "none",
                width: "100%",
                height: "42px",
                padding: "0 38px 0 13px",
                borderRadius: "9px",
                border: `1px solid ${COLORS.border}`,
                background: COLORS.white,
                fontSize: "14px",
                cursor: "pointer",
                outline: "none",
              }}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === "ALL"
                    ? "All Status"
                    : getStatusLabel(status)}
                </option>
              ))}
            </select>

            <ChevronDown
              size={17}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: COLORS.muted,
              }}
            />
          </div>

          {(search || statusFilter !== "ALL") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatusFilter("ALL");
              }}
              style={{
                height: "42px",
                padding: "0 14px",
                borderRadius: "9px",
                border: `1px solid ${COLORS.border}`,
                background: COLORS.white,
                color: COLORS.muted,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            padding: "14px 16px",
            borderRadius: "10px",
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            color: COLORS.danger,
            marginBottom: "18px",
            fontSize: "14px",
          }}
        >
          <AlertCircle size={19} />
          <div>
            <strong>Unable to load applications</strong>
            <div style={{ marginTop: "3px" }}>{error}</div>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div
        style={{
          background: COLORS.white,
          border: `1px solid ${COLORS.border}`,
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 3px 12px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            padding: "17px 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "17px",
                fontWeight: 800,
              }}
            >
              Applications
            </h2>

            <span
              style={{
                display: "block",
                marginTop: "4px",
                color: COLORS.muted,
                fontSize: "12px",
              }}
            >
              Showing {filteredApplications.length} of{" "}
              {applications.length} applications
            </span>
          </div>
        </div>

        {loading ? (
          <LoadingState />
        ) : filteredApplications.length === 0 ? (
          <EmptyState
            search={search}
            statusFilter={statusFilter}
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                minWidth: "1450px",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#FAFAFC",
                  }}
                >
                  <TableHeader>Application</TableHeader>
                  <TableHeader>Trainer</TableHeader>
                  <TableHeader>Job</TableHeader>
                  <TableHeader>Skills</TableHeader>
                  <TableHeader>Experience</TableHeader>
                  <TableHeader>Location</TableHeader>
                  <TableHeader>Applied On</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Resume</TableHeader>
                  <TableHeader>Action</TableHeader>
                </tr>
              </thead>

              <tbody>
                {filteredApplications.map((application, index) => {
                  const applicationId =
                    getApplicationId(application);

                  const trainerId = getTrainerId(application);

                  const skills = getSkills(application);

                  return (
                    <tr
                      key={
                        applicationId ||
                        `${getJobId(application)}-${trainerId}-${index}`
                      }
                      style={{
                        borderTop: `1px solid ${COLORS.border}`,
                      }}
                    >
                      {/* APPLICATION */}
                      <TableCell>
                        <div
                          style={{
                            fontWeight: 800,
                            color: COLORS.primary,
                            fontSize: "13px",
                          }}
                        >
                          {application?.applicationNumber ||
                            `APP-${applicationId || "—"}`}
                        </div>

                        <div
                          style={{
                            marginTop: "4px",
                            color: COLORS.muted,
                            fontSize: "11px",
                          }}
                        >
                          ID: {applicationId || "—"}
                        </div>
                      </TableCell>

                      {/* TRAINER */}
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "flex-start",
                          }}
                        >
                          <div
                            style={{
                              width: "36px",
                              height: "36px",
                              flexShrink: 0,
                              borderRadius: "50%",
                              background: "#F3E8FF",
                              color: COLORS.primary,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <User size={18} />
                          </div>

                          <div>
                            <div
                              style={{
                                fontWeight: 800,
                                fontSize: "13px",
                              }}
                            >
                              {getTrainerName(application)}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                marginTop: "5px",
                                color: COLORS.muted,
                                fontSize: "11px",
                              }}
                            >
                              <Mail size={12} />
                              {getTrainerEmail(application)}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                marginTop: "3px",
                                color: COLORS.muted,
                                fontSize: "11px",
                              }}
                            >
                              <Phone size={12} />
                              {getTrainerPhone(application)}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      {/* JOB */}
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                          }}
                        >
                          <BriefcaseBusiness
                            size={17}
                            color={COLORS.primary}
                          />

                          <div>
                            <div
                              style={{
                                fontWeight: 800,
                                fontSize: "13px",
                              }}
                            >
                              {getJobTitle(application)}
                            </div>

                            <div
                              style={{
                                marginTop: "4px",
                                color: COLORS.muted,
                                fontSize: "11px",
                              }}
                            >
                              Job ID: {getJobId(application) || "—"}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      {/* SKILLS */}
                      <TableCell>
                        {skills.length > 0 ? (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "5px",
                              maxWidth: "230px",
                            }}
                          >
                            {skills.slice(0, 6).map((skill, skillIndex) => (
                              <span
                                key={`${skill}-${skillIndex}`}
                                style={{
                                  padding: "4px 7px",
                                  borderRadius: "5px",
                                  background: "#F3E8FF",
                                  color: COLORS.primary,
                                  fontSize: "10px",
                                  fontWeight: 700,
                                }}
                              >
                                {skill}
                              </span>
                            ))}

                            {skills.length > 6 && (
                              <span
                                style={{
                                  padding: "4px 7px",
                                  borderRadius: "5px",
                                  background: "#F3F4F6",
                                  color: COLORS.muted,
                                  fontSize: "10px",
                                  fontWeight: 700,
                                }}
                              >
                                +{skills.length - 6}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: COLORS.muted }}>—</span>
                        )}
                      </TableCell>

                      {/* EXPERIENCE */}
                      <TableCell>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {getExperience(application)}
                        </span>
                      </TableCell>

                      {/* LOCATION */}
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "12px",
                          }}
                        >
                          <MapPin
                            size={15}
                            color={COLORS.primary}
                          />
                          {getJobLocation(application)}
                        </div>
                      </TableCell>

                      {/* APPLIED */}
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <CalendarDays
                            size={15}
                            color={COLORS.primary}
                          />
                          {formatDate(getAppliedDate(application))}
                        </div>
                      </TableCell>

                      {/* STATUS */}
                     {/* STATUS DROPDOWN */}
<TableCell>
  <select
    value={getApplicationStatus(application)}
    disabled={
      String(updatingApplicationId) ===
      String(getApplicationId(application))
    }
    onChange={(event) =>
      handleApplicationStatusChange(application, event.target.value)
    }
    aria-label={`Change application status for ${getTrainerName(application)}`}
    style={{
      minWidth: "145px",
      padding: "9px 10px",
      border: `1px solid ${COLORS.border}`,
      borderRadius: "8px",
      background: COLORS.white,
      color: COLORS.primary,
      fontSize: "12px",
      fontWeight: 700,
      cursor: "pointer",
      outline: "none",
      opacity:
        String(updatingApplicationId) ===
        String(getApplicationId(application))
          ? 0.6
          : 1,
    }}
  >
    <option value="PENDING">Pending</option>
    <option value="SHORTLISTED">Shortlisted</option>
    <option value="INTERVIEW">Interview</option>
    <option value="SELECTED">Selected</option>
    <option value="REJECTED">Rejected</option>
    <option value="WITHDRAWN">Withdrawn</option>
    <option value="NOT_DISCLOSED">Not Disclosed</option>
  </select>
</TableCell>

                      {/* RESUME */}
                      <TableCell>
                        {application?.resumeName ? (
                          <span
                            title={application.resumeName}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              maxWidth: "150px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "11px",
                              color: COLORS.primary,
                              fontWeight: 700,
                            }}
                          >
                            <FileText size={15} />
                            {application.resumeName}
                          </span>
                        ) : (
                          <span
                            style={{
                              color: COLORS.muted,
                              fontSize: "12px",
                            }}
                          >
                            No Resume
                          </span>
                        )}
                      </TableCell>

                      {/* ACTION */}
                      <TableCell>
                        <button
                          type="button"
                          onClick={() =>
                            openApplication(application)
                          }
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            border: "none",
                            borderRadius: "7px",
                            padding: "8px 11px",
                            background: COLORS.primary,
                            color: COLORS.white,
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: 700,
                          }}
                        >
                          <Eye size={15} />
                          View
                        </button>
                      </TableCell>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      {selectedApplication && (
        <ApplicationModal
          application={selectedApplication}
          onClose={closeApplication}
        />
      )}

      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
    <PartnerFooter />
    </>
  );
};

/* =========================
   SUMMARY CARD
========================= */

const SummaryCard = ({
  title,
  value,
  icon: Icon,
  background,
  iconColor,
}) => (
  <div
    style={{
      background: COLORS.white,
      border: `1px solid ${COLORS.border}`,
      borderRadius: "14px",
      padding: "17px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
    }}
  >
    <div>
      <div
        style={{
          color: COLORS.muted,
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: "5px",
          fontSize: "24px",
          fontWeight: 800,
          color: COLORS.text,
        }}
      >
        {value}
      </div>
    </div>

    <div
      style={{
        width: "42px",
        height: "42px",
        borderRadius: "10px",
        background,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon size={21} />
    </div>
  </div>
);

/* =========================
   TABLE HELPERS
========================= */

const TableHeader = ({ children }) => (
  <th
    style={{
      padding: "13px 14px",
      textAlign: "left",
      fontSize: "11px",
      textTransform: "uppercase",
      letterSpacing: "0.4px",
      color: COLORS.muted,
      fontWeight: 800,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

const TableCell = ({ children }) => (
  <td
    style={{
      padding: "14px",
      verticalAlign: "top",
      fontSize: "13px",
      color: COLORS.text,
    }}
  >
    {children}
  </td>
);

/* =========================
   LOADING
========================= */

const LoadingState = () => (
  <div
    style={{
      minHeight: "300px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: "12px",
      color: COLORS.muted,
    }}
  >
    <RefreshCw
      size={28}
      color={COLORS.primary}
      style={{
        animation: "spin 1s linear infinite",
      }}
    />

    <span style={{ fontSize: "14px" }}>
      Loading applications...
    </span>
  </div>
);

/* =========================
   EMPTY STATE
========================= */

const EmptyState = ({ search, statusFilter }) => (
  <div
    style={{
      minHeight: "280px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      padding: "30px",
      textAlign: "center",
    }}
  >
    <div
      style={{
        width: "58px",
        height: "58px",
        borderRadius: "50%",
        background: "#F3E8FF",
        color: COLORS.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "13px",
      }}
    >
      <FileText size={26} />
    </div>

    <h3
      style={{
        margin: 0,
        fontSize: "17px",
      }}
    >
      No applications found
    </h3>

    <p
      style={{
        margin: "7px 0 0",
        color: COLORS.muted,
        fontSize: "13px",
        maxWidth: "400px",
      }}
    >
      {search || statusFilter !== "ALL"
        ? "Try changing your search or status filter."
        : "There are no applications for your jobs yet."}
    </p>
  </div>
);

/* =========================
   APPLICATION MODAL
========================= */

const ApplicationModal = ({ application, onClose }) => {
  const skills = getSkills(application);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "850px",
          maxHeight: "90vh",
          overflowY: "auto",
          background: COLORS.white,
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* MODAL HEADER */}
        <div
          style={{
            padding: "20px 22px",
            background: COLORS.primary,
            color: COLORS.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "15px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "12px",
                opacity: 0.8,
                marginBottom: "5px",
              }}
            >
              APPLICATION
            </div>

            <h2
              style={{
                margin: 0,
                fontSize: "21px",
              }}
            >
              {application?.applicationNumber ||
                `APP-${getApplicationId(application) || "—"}`}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "rgba(255,255,255,0.15)",
              color: COLORS.white,
              width: "36px",
              height: "36px",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: "22px" }}>
          {/* TRAINER */}
          <ModalSection title="Trainer Details" icon={User}>
            <DetailGrid>
              <DetailItem
                label="Trainer Name"
                value={getTrainerName(application)}
              />

              <DetailItem
                label="Trainer ID"
                value={getTrainerId(application) || "—"}
              />

              <DetailItem
                label="Email"
                value={getTrainerEmail(application)}
              />

              <DetailItem
                label="Phone"
                value={getTrainerPhone(application)}
              />

              <DetailItem
                label="Experience"
                value={getExperience(application)}
              />
            </DetailGrid>
          </ModalSection>

          {/* JOB */}
          <ModalSection title="Job Details" icon={BriefcaseBusiness}>
            <DetailGrid>
              <DetailItem
                label="Job Title"
                value={getJobTitle(application)}
              />

              <DetailItem
                label="Job ID"
                value={getJobId(application) || "—"}
              />

              <DetailItem
                label="Location"
                value={getJobLocation(application)}
              />

              <DetailItem
                label="Applied On"
                value={formatDateTime(
                  getAppliedDate(application)
                )}
              />

              <DetailItem
                label="Status"
                value={
                  <StatusBadge
                    status={application?.applicationStatus}
                  />
                }
              />
            </DetailGrid>

            {skills.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 800,
                    color: COLORS.muted,
                    marginBottom: "8px",
                  }}
                >
                  SKILLS
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "7px",
                  }}
                >
                  {skills.map((skill, index) => (
                    <span
                      key={`${skill}-${index}`}
                      style={{
                        background: "#F3E8FF",
                        color: COLORS.primary,
                        padding: "6px 9px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: 700,
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </ModalSection>

          {/* COVER LETTER */}
          <ModalSection title="Cover Letter" icon={FileText}>
            <div
              style={{
                background: "#FAFAFC",
                border: `1px solid ${COLORS.border}`,
                borderRadius: "9px",
                padding: "14px",
                fontSize: "13px",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
              }}
            >
              {application?.coverLetter ||
                application?.cover_letter ||
                "No cover letter provided."}
            </div>
          </ModalSection>

          {/* RESUME */}
          <ModalSection title="Resume" icon={FileText}>
            <div
              style={{
                padding: "13px",
                border: `1px solid ${COLORS.border}`,
                borderRadius: "9px",
                fontSize: "13px",
              }}
            >
              <strong>Resume:</strong>{" "}
              {application?.resumeName ||
                application?.resume_name ||
                "No resume available"}
            </div>
          </ModalSection>

          {/* REMARKS */}
          <ModalSection title="Remarks" icon={AlertCircle}>
            <div
              style={{
                background: "#FAFAFC",
                border: `1px solid ${COLORS.border}`,
                borderRadius: "9px",
                padding: "14px",
                fontSize: "13px",
                lineHeight: 1.6,
              }}
            >
              {application?.remarks || "No remarks added."}
            </div>
          </ModalSection>
        </div>
      </div>
    </div>
  );
};

const ModalSection = ({ title, icon: Icon, children }) => (
  <section
    style={{
      marginBottom: "22px",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "10px",
        color: COLORS.primary,
      }}
    >
      <Icon size={17} />

      <h3
        style={{
          margin: 0,
          fontSize: "15px",
          fontWeight: 800,
        }}
      >
        {title}
      </h3>
    </div>

    {children}
  </section>
);

const DetailGrid = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit, minmax(210px, 1fr))",
      gap: "13px",
    }}
  >
    {children}
  </div>
);

const DetailItem = ({ label, value }) => (
  <div
    style={{
      padding: "12px",
      background: "#FAFAFC",
      border: `1px solid ${COLORS.border}`,
      borderRadius: "8px",
    }}
  >
    <div
      style={{
        fontSize: "10px",
        color: COLORS.muted,
        textTransform: "uppercase",
        fontWeight: 800,
        marginBottom: "5px",
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontSize: "13px",
        fontWeight: 600,
        wordBreak: "break-word",
      }}
    >
      {value}
    </div>
  </div>

);

export default JobApplication;